/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import {
    displayNames,
    featureDescriptions,
    translateFeatureText,
} from '../../Teto_vs_egen/Analyze/translateFeatureNames';

const humanizeLevel = (level, t) => {
    return t(`face_result.values.${level}`, { defaultValue: level || '-' });
};

const getFeatureComment = (feature, t, isEnglish) => {
    const translated = t(`face_result.descs.${feature?.name}`, { defaultValue: '' });
    if (translated) return translated;
    if (!isEnglish && feature?.description) return feature.description;
    if (!isEnglish && feature?.name && featureDescriptions[feature.name]) return featureDescriptions[feature.name];
    return t('face_result.detail.metrics.default_comment');
};

const buildGuideCards = (actionPlan, t) => ([
    {
        key: 'hair',
        title: t('face_result.detail.guide.hair'),
        body: actionPlan?.hair || t('face_result.detail.guide.hair_fallback'),
    },
    {
        key: 'fashion',
        title: t('face_result.detail.guide.style'),
        body: actionPlan?.fashion || t('face_result.detail.guide.style_fallback'),
    },
    {
        key: 'tip',
        title: t('face_result.detail.guide.profile_photo'),
        body: actionPlan?.tip || t('face_result.detail.guide.profile_fallback'),
    },
]);

const ExpandableBlock = ({ title, children, defaultOpen = false }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div style={expandableStyle}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                style={expandableButtonStyle}
            >
                <span>{title}</span>
                <span style={{ color: '#60a5fa', fontSize: '0.8rem' }}>{open ? t('face_result.detail.common.collapse') : t('face_result.detail.common.view')}</span>
            </button>
            {open ? <div style={{ marginTop: '0.9rem' }}>{children}</div> : null}
        </div>
    );
};

const MasculinityDetailSection = ({ result, compact = false }) => {
    const { t, i18n } = useTranslation();
    const isEnglish = String(i18n.resolvedLanguage || i18n.language).startsWith('en');
    const [showAllFeatures, setShowAllFeatures] = useState(false);

    if (!result) return null;

    const features = Array.isArray(result?.features)
        ? result.features
        : Array.isArray(result?.result?.features)
            ? result.result.features
            : [];

    const gptOutput = result?.gpt_output || result?.result?.gpt_output || '';
    const masculinityResult = result?.masculinity_result || {};
    const masculinityText = [
        masculinityResult?.title,
        masculinityResult?.summary,
        masculinityResult?.face_description,
        masculinityResult?.character,
    ]
        .filter(Boolean)
        .join('\n\n');
    const displayMasculinityText = isEnglish && /[가-힣]/.test(masculinityText)
        ? t('face_result.detail.metrics.masculinity_fallback')
        : masculinityText;
    const displayGptOutput = isEnglish && /[가-힣]/.test(gptOutput)
        ? t('face_result.detail.metrics.face_shape_fallback')
        : translateFeatureText(gptOutput);

    const actionGuides = useMemo(
        () => buildGuideCards(result?.persona?.analysis_report?.action_plan || {}, t),
        [result, t]
    );

    const prioritizedFeatures = useMemo(() => {
        return features.map((feature, index) => ({
            ...feature,
            key: `${feature?.name || 'feature'}-${index}`,
            label: t(`face_result.labels.${feature?.name}`, { defaultValue: isEnglish ? feature?.name : displayNames[feature?.name] || feature?.name || t('face_result.detail.visual.metric') }),
            levelLabel: humanizeLevel(feature?.level, t),
            comment: getFeatureComment(feature, t, isEnglish),
        }));
    }, [features, isEnglish, t]);

    const topStrengths = prioritizedFeatures.slice(0, 2);
    const cautionFeature = prioritizedFeatures[2] || null;
    const otherFeatures = compact ? [] : prioritizedFeatures.slice(3);

    const masculinityPercentile = Number(result?.masculinity_percentile);
    const safePercentile = Number.isFinite(masculinityPercentile)
        ? masculinityPercentile.toFixed(1)
        : '-';

    if (!masculinityText && prioritizedFeatures.length === 0 && !gptOutput) {
        return null;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: compact ? '0' : '1.25rem' }}>
            <div style={keyCardStyle}>
                <div style={eyebrowStyle}>{t('face_result.detail.slides.evidence')}</div>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {topStrengths.map((feature) => (
                        <div key={feature.key} style={strengthCardStyle}>
                            <div style={keyCardTitleStyle}>
                                {feature.label}: {feature.levelLabel}
                            </div>
                            <div style={keyCardBodyStyle}>{feature.comment}</div>
                        </div>
                    ))}

                    {cautionFeature ? (
                        <div style={cautionCardStyle}>
                            <div style={keyCardTitleStyle}>{t('face_result.detail.metrics.impression_point', { label: cautionFeature.label })}</div>
                            <div style={keyCardBodyStyle}>{cautionFeature.comment}</div>
                        </div>
                    ) : null}

                    <div style={neutralCardStyle}>
                        <div style={keyCardTitleStyle}>{t('face_result.detail.metrics.masculinity_score', { score: safePercentile })}</div>
                        <div style={keyCardBodyStyle}>
                            {t('face_result.detail.visual.masculinity_description')}
                        </div>
                    </div>
                </div>
            </div>

            {!compact ? (
                <div style={keyCardStyle}>
                    <div style={eyebrowStyle}>{t('face_result.detail.slides.guide')}</div>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {actionGuides.map((guide) => (
                            <div key={guide.key} style={neutralCardStyle}>
                                <div style={keyCardTitleStyle}>{guide.title}</div>
                                <div style={keyCardBodyStyle}>{guide.body}</div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {!compact ? (
                <ExpandableBlock title={t('face_result.detail.metrics.view_all')}>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {otherFeatures.length > 0 ? otherFeatures.map((feature) => (
                            <div key={feature.key} style={softCardStyle}>
                                <div style={keyCardTitleStyle}>
                                    {feature.label}: {feature.levelLabel}
                                </div>
                                <div style={keyCardBodyStyle}>{feature.comment}</div>
                            </div>
                        )) : <div style={keyCardBodyStyle}>{t('face_result.detail.metrics.no_more')}</div>}
                    </div>

                    {prioritizedFeatures.length > 3 ? (
                        <button
                            type="button"
                            onClick={() => setShowAllFeatures((prev) => !prev)}
                            style={toggleButtonStyle}
                        >
                            {showAllFeatures
                                ? t('face_result.detail.metrics.show_key')
                                : t('face_result.detail.metrics.expand_all', { count: prioritizedFeatures.length })}
                        </button>
                    ) : null}

                    {showAllFeatures ? (
                        <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.85rem' }}>
                            {prioritizedFeatures.map((feature) => (
                                <div key={`${feature.key}-full`} style={softCardStyle}>
                                    <div style={keyCardTitleStyle}>
                                        {feature.label}: {feature.levelLabel}
                                    </div>
                                    <div style={keyCardBodyStyle}>{feature.comment}</div>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </ExpandableBlock>
            ) : null}

            {!compact && displayMasculinityText ? (
                <ExpandableBlock title={t('face_result.detail.metrics.masculinity_details')}>
                    <div style={keyCardBodyStyle}>
                        <ReactMarkdown>{displayMasculinityText}</ReactMarkdown>
                    </div>
                </ExpandableBlock>
            ) : null}

            {!compact && gptOutput ? (
                <ExpandableBlock title={t('face_result.detail.metrics.face_shape_details')}>
                    <div style={keyCardBodyStyle}>
                        <ReactMarkdown>{displayGptOutput}</ReactMarkdown>
                    </div>
                </ExpandableBlock>
            ) : null}
        </div>
    );
};

const expandableStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '18px',
    padding: '1rem',
};

const expandableButtonStyle = {
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: '#f8fafc',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 0,
    cursor: 'pointer',
    fontSize: '0.92rem',
    fontWeight: 700,
};

const keyCardStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '18px',
    padding: '1rem',
};

const eyebrowStyle = {
    fontSize: '0.78rem',
    color: '#60a5fa',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '0.7rem',
};

const keyCardTitleStyle = {
    color: '#f8fafc',
    fontWeight: 700,
    fontSize: '0.9rem',
    marginBottom: '0.3rem',
};

const keyCardBodyStyle = {
    color: '#94a3b8',
    fontSize: '0.84rem',
    lineHeight: 1.6,
};

const strengthCardStyle = {
    background: 'rgba(96,165,250,0.06)',
    border: '1px solid rgba(96,165,250,0.2)',
    borderRadius: '14px',
    padding: '0.95rem',
};

const cautionCardStyle = {
    background: 'rgba(251,191,36,0.06)',
    border: '1px solid rgba(251,191,36,0.2)',
    borderRadius: '14px',
    padding: '0.95rem',
};

const neutralCardStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: '14px',
    padding: '0.95rem',
};

const softCardStyle = {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '14px',
    padding: '0.9rem',
};

const toggleButtonStyle = {
    marginTop: '0.85rem',
    width: '100%',
    border: 'none',
    borderRadius: '12px',
    padding: '0.75rem',
    background: 'rgba(255,255,255,0.04)',
    color: '#93c5fd',
    cursor: 'pointer',
    fontWeight: 700,
};

export default MasculinityDetailSection;
